import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getBalanceTypeById, getPrimaryMemberBalance, logger } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { observer } from 'mobx-react';
import { useMemberContext } from '../../../context/member/hooks/use-member-context';
import { useI18n } from '../../../context/occ/hooks/use-i18n';
import { useOccConfig } from '../../../context/occ/hooks/use-occ-config';
import { useOccDisplayText } from '../../../context/occ/hooks/use-occ-display-text';
import { DisplayText } from '../../../subcomponents/display-text/display-text';
import { getFormattedMemberBalance } from '../../../utils/get-formatted-member-balance';
import { withComponentWrapper } from '../../../utils/with-component-wrapper';
import { MemberBalanceSkeleton } from './subcomponents/skeleton';
import { MemberBalancePrimaryValue } from './types/member-balance-primary-value';
import { defaultMemberBalanceProps, memberBalancePropsValidator } from './types/props';
import { getMonetaryValueLabelForMemberBalance } from './utils/get-monetary-value-label-for-member-balance';
const b = bemClassBuilder('v-member-balance');
export const MemberBalance = observer(props => {
    const { member: memberDisplayText } = useOccDisplayText();
    const { enableSkeletonLoadingIndicators } = useOccConfig();
    const { displayText, config } = props;
    const { member } = useMemberContext();
    const i18n = useI18n();
    if (!member) {
        return enableSkeletonLoadingIndicators ? _jsx(MemberBalanceSkeleton, {}) : null;
    }
    const balance = getPrimaryMemberBalance();
    if (!balance) {
        logger.error(`member ${member.id} does not have default balance type configured`);
        return null;
    }
    const balanceType = getBalanceTypeById(balance.balanceTypeId);
    if (!balanceType) {
        logger.error(`No balance type for balanceTypeId: ${balance.balanceTypeId}`);
        return null;
    }
    // TODO: Add support for multiple balances and expiries. #286581
    const expiry = balance.expiries[0];
    const balanceUnits = i18n.template(displayText.availableUnitsFormat, {
        availableUnits: getFormattedMemberBalance(i18n, memberDisplayText, balance.availableUnits, balanceType.unitName)
    });
    const monetaryValue = getMonetaryValueLabelForMemberBalance(i18n, displayText.monetaryValueFormat, balance.availableUnits, balanceType.paymentConversionRate);
    const primaryValue = !monetaryValue || config.primaryValue === MemberBalancePrimaryValue.BalanceUnits ? balanceUnits : monetaryValue;
    const secondaryValue = monetaryValue
        ? config.primaryValue === MemberBalancePrimaryValue.BalanceUnits
            ? monetaryValue
            : balanceUnits
        : undefined;
    const showSecondaryValue = config.showSecondaryValue && secondaryValue;
    return (_jsxs("div", Object.assign({ className: b.block() }, { children: [_jsxs("div", Object.assign({ className: b.element('values-container') }, { children: [_jsx(DisplayText, { className: b.element('primary-value', { [config.primaryValue]: true }), label: displayText.primaryValueFormat, templateParameters: { primaryValue } }), showSecondaryValue && (_jsx(DisplayText, { className: b.element('secondary-value'), label: displayText.secondaryValueFormat, templateParameters: { secondaryValue } }))] })), _jsx(DisplayText, { className: b.element('club-name'), label: balanceType.name }), expiry && (_jsx(DisplayText, { className: b.element('expiry-details'), label: displayText.expiringUnitsFormat, templateParameters: {
                    expiringUnits: getFormattedMemberBalance(i18n, memberDisplayText, expiry.units, balanceType.unitName),
                    expiresAt: expiry.expiresAt
                } }))] })));
});
/**
 * The **Member Balance** component renders the details of the primary balance for currently logged-in Loyalty member.
 */
export const MemberBalanceComponent = withComponentWrapper(MemberBalance, defaultMemberBalanceProps, memberBalancePropsValidator);
